import React, { useState, useEffect, useRef } from 'react';
import SignaturePad from 'signature_pad';
import { showMessage } from '../../../scripts/showMessage.js';
import { Trash2 } from 'lucide-react';

const labelStyle = {
    display: 'block',
    marginBottom: '0.375rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: 'var(--text-secondary, #64748b)'
};

const inputStyle = {
    width: '100%',
    padding: '0.625rem 0.875rem',
    border: '1px solid var(--border-medium, #cbd5e1)',
    borderRadius: '8px',
    fontSize: '0.9375rem',
    color: 'var(--text-primary, #2c3e50)',
    background: 'var(--bg-card, #ffffff)',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    outline: 'none'
};

const StepSignature = ({ initialSignature, onComplete }) => {
    const canvasRef = useRef(null);
    const signaturePadRef = useRef(null);
    const [signName, setSignName] = useState(initialSignature?.name || '');
    const [isSaving, setIsSaving] = useState(false);
    const [showValidation, setShowValidation] = useState(false);
    const [hasSignature, setHasSignature] = useState(false);

    useEffect(() => {
        if (canvasRef.current) {
            const pad = new SignaturePad(canvasRef.current, {
                backgroundColor: 'rgb(255, 255, 255)',
                penColor: 'rgb(0, 0, 0)',
                onEnd: () => {
                    setHasSignature(true);
                }
            });

            signaturePadRef.current = pad;
        }

        return () => {
            if (signaturePadRef.current) {
                signaturePadRef.current.off();
            }
        };
    }, []);

    const handleClear = () => {
        if (signaturePadRef.current) {
            signaturePadRef.current.clear();
            setHasSignature(false);
        }
    };

    const handleSave = async () => {
        setShowValidation(true);

        if (!signName.trim()) {
            showMessage('Bitte Vor- und Nachname eingeben', 'error');
            return;
        }

        if (!hasSignature && signaturePadRef.current?.isEmpty()) {
            showMessage('Bitte eine Unterschrift zeichnen', 'error');
            return;
        }

        setIsSaving(true);

        try {
            const signData = signaturePadRef.current.toDataURL();
            const signEncoded = signData.replace(/\+/g, '[plus]');

            const formData = new FormData();
            formData.append('name', signName);
            formData.append('sign', signEncoded);
            formData.append('checkSum', '1');

            const response = await fetch('/index.php/contracts/signUpdate', {
                method: 'POST',
                body: formData
            });

            const data = await response.json();

            if (data.success) {
                showMessage('Unterschrift erfolgreich gespeichert', 'success');
                onComplete();
            } else {
                showMessage('Fehler beim Speichern der Unterschrift', 'error');
            }
        } catch (error) {
            console.error('Error saving signature:', error);
            showMessage('Fehler beim Speichern der Unterschrift', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div style={{ padding: '1.5rem' }}>
            <p style={{
                fontSize: '0.9375rem',
                color: 'var(--text-secondary, #64748b)',
                marginBottom: '1.5rem',
                lineHeight: '1.6'
            }}>
                Diese Unterschrift wird für die Einverständniserklärung der Kunden sowie
                für die Vertragsfunktion benötigt.
            </p>

            {/* Existing signature preview */}
            {initialSignature?.exists && (
                <div style={{
                    marginBottom: '1.5rem',
                    padding: '1rem',
                    background: 'var(--bg-secondary, #f5f7f9)',
                    border: '1px solid var(--border-light, #e2e8f0)',
                    borderRadius: '12px'
                }}>
                    <label style={{ ...labelStyle, marginBottom: '0.75rem' }}>Aktuelle Unterschrift</label>
                    <img
                        src={initialSignature.url}
                        alt="Aktuelle Unterschrift"
                        style={{
                            maxWidth: '467px',
                            height: 'auto',
                            border: '1px solid var(--border-light, #e2e8f0)',
                            borderRadius: '8px',
                            background: '#ffffff'
                        }}
                    />
                </div>
            )}

            {/* Name input */}
            <div style={{ maxWidth: '400px', marginBottom: '1.25rem' }}>
                <label style={labelStyle}>
                    Vor- und Nachname
                    <span style={{ color: 'var(--danger, #dc3545)', marginLeft: '2px' }}>*</span>
                </label>
                <input
                    type="text"
                    value={signName}
                    onChange={(e) => setSignName(e.target.value)}
                    style={{
                        ...inputStyle,
                        borderColor: showValidation && !signName.trim() ? 'var(--danger, #dc3545)' : 'var(--border-medium, #cbd5e1)'
                    }}
                    placeholder="Max Mustermann"
                />
            </div>

            {/* Signature canvas */}
            <div style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>
                    Unterschrift
                    <span style={{ color: 'var(--danger, #dc3545)', marginLeft: '2px' }}>*</span>
                </label>
                <div style={{
                    display: 'inline-block',
                    border: showValidation && !hasSignature
                        ? '2px solid var(--danger, #dc3545)'
                        : '2px solid var(--border-medium, #cbd5e1)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    background: '#ffffff'
                }}>
                    <canvas
                        ref={canvasRef}
                        width={467}
                        height={100}
                        style={{ display: 'block', cursor: 'crosshair' }}
                    />
                </div>
            </div>

            {/* Clear button */}
            <div style={{ marginBottom: '1.5rem' }}>
                <button
                    onClick={handleClear}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        padding: '0.5rem 1rem',
                        border: '1px solid var(--border-medium, #cbd5e1)',
                        borderRadius: '8px',
                        background: 'var(--bg-card, #ffffff)',
                        color: 'var(--text-secondary, #64748b)',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                    }}
                >
                    <Trash2 size={16} />
                    Löschen
                </button>
            </div>

            {/* Save button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.625rem 1.5rem',
                        border: 'none',
                        borderRadius: '8px',
                        background: 'var(--accent-slate, #5b7c8d)',
                        color: 'white',
                        fontSize: '0.9375rem',
                        fontWeight: '600',
                        cursor: isSaving ? 'wait' : 'pointer',
                        opacity: isSaving ? 0.7 : 1,
                        transition: 'all 0.2s ease'
                    }}
                >
                    {isSaving ? 'Speichert...' : 'Speichern & Weiter'}
                </button>
            </div>
        </div>
    );
};

export default StepSignature;
